import { THEMES, ThemeId, Orientation } from '../themes';
import { IconEdit, IconPresentation } from './icons';
import type { EditorMode } from './layout';

/* =========================================================
   OrientationPicker — toggle Portrait / Paysage A4.
   ========================================================= */
export function OrientationPicker({
  value,
  locked,
  onChange,
}: {
  value: Orientation;
  locked: Orientation | null;
  onChange: (o: Orientation) => void;
}) {
  const effective = locked ?? value;
  return (
    <div
      className="orientation-picker"
      role="group"
      aria-label="Orientation"
      title={locked ? `Orientation verrouillée par le masque PDF custom (${locked})` : undefined}
      data-locked={locked ? '1' : undefined}
    >
      <button
        type="button"
        className={`op-btn ${effective === 'portrait' ? 'active' : ''}`}
        onClick={() => !locked && onChange('portrait')}
        aria-pressed={effective === 'portrait'}
        disabled={!!locked}
        title="Portrait A4"
      >
        <svg width="14" height="18" viewBox="0 0 14 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <rect x="1" y="1" width="12" height="16" rx="1.2" />
        </svg>
        Portrait A4
      </button>
      <button
        type="button"
        className={`op-btn ${effective === 'landscape' ? 'active' : ''}`}
        onClick={() => !locked && onChange('landscape')}
        aria-pressed={effective === 'landscape'}
        disabled={!!locked}
        title="Paysage A4"
      >
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <rect x="1" y="1" width="16" height="12" rx="1.2" />
        </svg>
        Paysage A4
      </button>
    </div>
  );
}

/* =========================================================
   ModePicker — bascule Édition / Présentation.
   ========================================================= */
export function ModePicker({
  value,
  onChange,
}: {
  value: EditorMode;
  onChange: (m: EditorMode) => void;
}) {
  return (
    <div className="mode-picker" role="group" aria-label="Mode d'affichage">
      <button
        type="button"
        className={`op-btn ${value === 'edition' ? 'active' : ''}`}
        onClick={() => onChange('edition')}
        aria-pressed={value === 'edition'}
        title="Mode édition — blocs compacts, DnD pleinement visible"
      >
        <IconEdit />
        Édition
      </button>
      <button
        type="button"
        className={`op-btn ${value === 'presentation' ? 'active' : ''}`}
        onClick={() => onChange('presentation')}
        aria-pressed={value === 'presentation'}
        title="Mode présentation — rendu WYSIWYG, DnD discret"
      >
        <IconPresentation />
        Présentation
      </button>
    </div>
  );
}

/* =========================================================
   ThemeSwatches — les 12 pastilles partagées.
   ========================================================= */
export function ThemeSwatches({
  value,
  onChange,
}: {
  value: ThemeId;
  onChange: (t: ThemeId) => void;
}) {
  return (
    <div className="theme-swatches" role="group" aria-label="Thème">
      {THEMES.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          title={`${t.label} — ${t.hint}`}
          aria-pressed={value === t.id}
          className="theme-swatch"
          data-active={value === t.id ? '1' : undefined}
          style={{ background: t.swatch }}
        />
      ))}
    </div>
  );
}
